import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import { getAssetColorProperties } from 'js/src/agent/video-state/serialization/assetHelpers';
import { checkpointVideo } from 'js/src/data/video-data';
import { setObjectPropertyValue } from 'js/src/editor/util';
import { intToHex, parseHexColor } from 'js/src/libs/color-utils';
import { saveRawLuminary } from 'js/src/luminary';

export const setAssetColorSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe('The asset name (e.g. color_1)'),
    property: z
        .string()
        .describe(
            'The color property to set, named exactly as the video state lists it for this asset (e.g. color, lineColor, colorStops.0.color).',
        ),
    color: z
        .string()
        .regex(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)
        .describe('The color as a hex code.'),
});
export type SetAssetColorArgs = z.infer<typeof setAssetColorSchema>;

export class SetAssetColorTool extends VideoStateTool<typeof setAssetColorSchema> {
    protected override readonly schema = setAssetColorSchema;
    protected override readonly toolName = TOOL_NAMES.setAssetColor;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription =
        'Set one color property on an asset: a scene background, or a shape or gradient the design paints behind its content.';

    public override getToolDisplayName(): string {
        return 'Setting color';
    }

    public get statusIcon(): string {
        return 'wallpaper';
    }

    public async execute(args: SetAssetColorArgs, context: ToolExecutionContext) {
        const { asset_name, property, color } = args;
        const { parentActionGroup } = context;

        const colorInt = parseHexColor(color);
        if (colorInt === null) {
            return 'Error: color must be a hex string like "#ff0000"';
        }

        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];

            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId}`;
            }

            // The video state lists a background's colour as `color`, but it is stored as sourceColor.
            const resolvedProperty =
                VideoData.isColorAsset(asset) && property === 'color' ? 'sourceColor' : property;
            const colorProperties = getAssetColorProperties(asset);
            if (colorProperties.length === 0) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} has no color this tool can set`;
            }
            if (!colorProperties.includes(resolvedProperty)) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} has no color property ${property}. It takes: ${colorProperties.join(', ')}`;
            }

            try {
                setObjectPropertyValue(asset, resolvedProperty, colorInt);

                // Save changes to enable undo/redo functionality
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            } catch (error) {
                return `Error: Failed to set ${property} for asset ${asset_name} in scene ${sceneShortId}`;
            }
            return `Success: Set ${property} for scene ${sceneShortId}, asset ${asset_name} to ${intToHex(colorInt)}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting color: ${errorMessage}`;
        }
    }
}
